import React, { useEffect, useRef, useState } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { useNotifications } from '../contexts/NotificationContext'
import { collection, getDocs, doc, getDoc } from 'firebase/firestore'
import { db } from '../firebase'

// Monitoring control
let isMonitoringDisabled = false

const GlobalMonitoring = () => {
  const { userClaims } = useAuth()
  const { addNotification, requestPermission } = useNotifications()
  const [devices, setDevices] = useState([])
  const [deviceStatuses, setDeviceStatuses] = useState({})
  const [lastAlerts, setLastAlerts] = useState({})
  const initializedCustomerRef = useRef(null)

  // Check if monitoring is disabled
  useEffect(() => {
    if (isMonitoringDisabled) {
      console.log('Global monitoring: Disabled, skipping...')
      return
    }
  }, [])

  // Calculate stock level for a device
  const calculateStockLevel = (device) => {
    if (!device.slots || Object.keys(device.slots).length === 0) {
      return { percentage: 0, totalCapacity: 0, currentQuantity: 0 }
    }

    let totalCapacity = 0
    let currentQuantity = 0

    Object.values(device.slots).forEach(slot => {
      const capacity = slot.capacity || slot.quantity || 0
      const quantity = slot.quantity || 0
      totalCapacity += capacity
      currentQuantity += quantity
    })

    const percentage = totalCapacity > 0 ? Math.round((currentQuantity / totalCapacity) * 100) : 0
    return { percentage, totalCapacity, currentQuantity }
  }

  // Fetch devices from Firebase
  const fetchDevices = async (customer) => {
    if (!customer) return

    try {
      const collectionName = `${customer}_machines`
      const devicesRef = collection(db, collectionName)
      const snapshot = await getDocs(devicesRef)
      
      const deviceList = []
      snapshot.forEach((doc) => {
        deviceList.push({
          id: doc.id,
          ...doc.data()
        })
      })

      setDevices(deviceList)
      console.log(`Global monitoring: Found ${deviceList.length} devices for customer: ${customer}`)
    } catch (err) {
      console.error('Global monitoring: Error fetching devices:', err)
      if (err.code === 'permission-denied') {
        console.log('Global monitoring: Permission denied - this may be due to authentication or AppCheck issues')
      }
    }
  }

  // Fetch device statuses with fallback
  const fetchDeviceStatuses = async (customer) => {
    if (!customer) return

    try {
      // Try primary device_state collection
      const statusRef = doc(db, `${customer}_device_state`, customer)
      const statusDoc = await getDoc(statusRef)
      
      if (statusDoc.exists()) {
        const statusData = statusDoc.data()
        const statuses = {}
        
        Object.entries(statusData).forEach(([deviceId, deviceInfo]) => {
          if (deviceInfo && typeof deviceInfo === 'object' && deviceInfo.status) {
            statuses[deviceId] = deviceInfo.status
          }
        })
        
        setDeviceStatuses(statuses)
        console.log(`Global monitoring: Device statuses loaded for ${Object.keys(statuses).length} devices`)
        return
      }
    } catch (err) {
      console.error('Global monitoring: Error fetching device statuses:', err)
      if (err.code === 'permission-denied') {
        console.log('Global monitoring: Permission denied for device_state collection - using fallback method')
      }
    }
    
    // Fallback: Use maintenance_mode field from device data as status indicator
    try {
      const fallbackStatuses = {}
      devices.forEach(device => {
        if (device.maintenance_mode === true) {
          fallbackStatuses[device.id] = 'maintenance'
        } else {
          // Assume online if device exists and not in maintenance
          fallbackStatuses[device.id] = 'online'
        }
      })
      
      if (Object.keys(fallbackStatuses).length > 0) {
        setDeviceStatuses(fallbackStatuses)
        console.log(`Global monitoring: Using fallback device statuses for ${Object.keys(fallbackStatuses).length} devices`)
      }
    } catch (fallbackErr) {
      console.error('Global monitoring: Fallback status check failed:', fallbackErr)
    }
  }

  // Check for stock alerts
  const checkStockAlerts = () => {
    if (!devices.length) return

    devices.forEach(device => {
      const stockData = calculateStockLevel(device)
      const alertKey = `stock_${device.id}`
      const currentTime = Date.now()
      
      // Prevent duplicate alerts (wait 30 minutes between same alerts)
      if (lastAlerts[alertKey] && (currentTime - lastAlerts[alertKey]) < 30 * 60 * 1000) {
        return
      }

      let shouldAlert = false
      let priority = 'info'
      let title = ''
      let message = ''

      if (stockData.percentage === 0 && stockData.totalCapacity > 0) {
        shouldAlert = true
        priority = 'critical'
        title = 'Out of Stock Alert'
        message = `Device ${device.name || device.id} is completely out of stock`
      } else if (stockData.percentage < 20 && stockData.totalCapacity > 0) {
        shouldAlert = true
        priority = 'critical'
        title = 'Critical Stock Alert'
        message = `Device ${device.name || device.id} is critically low on stock (${stockData.percentage}%)`
      } else if (stockData.percentage < 50 && stockData.totalCapacity > 0) {
        shouldAlert = true
        priority = 'warning'
        title = 'Low Stock Warning'
        message = `Device ${device.name || device.id} is running low on stock (${stockData.percentage}%)`
      }

      if (shouldAlert) {
        addNotification({
          title,
          message,
          priority,
          category: 'stockAlerts',
          deviceId: device.id,
          stockPercentage: stockData.percentage
        })

        setLastAlerts(prev => ({
          ...prev,
          [alertKey]: currentTime
        }))

        console.log(`Global monitoring: Stock alert triggered for ${device.name || device.id} (${stockData.percentage}%)`)
      }
    })
  }

  // Check for device status alerts
  const checkDeviceStatusAlerts = () => {
    if (!devices.length || !Object.keys(deviceStatuses).length) return

    devices.forEach(device => {
      const status = deviceStatuses[device.id] || 'unknown'
      const alertKey = `status_${device.id}_${status}`
      const currentTime = Date.now()
      
      // Prevent duplicate alerts (wait 15 minutes between same status alerts)
      if (lastAlerts[alertKey] && (currentTime - lastAlerts[alertKey]) < 15 * 60 * 1000) {
        return
      }

      let shouldAlert = false
      let priority = 'info'
      let title = ''
      let message = ''

      if (status === 'down' || status === 'offline') {
        shouldAlert = true
        priority = 'warning'
        title = 'Device Offline'
        message = `Device ${device.name || device.id} has gone offline`
      } else if (status === 'maintenance') {
        shouldAlert = true
        priority = 'info'
        title = 'Device in Maintenance'
        message = `Device ${device.name || device.id} is in maintenance mode`
      } else if (status === 'up' || status === 'online') {
        // Only alert if device was previously offline or in maintenance
        const prevOfflineKey = `status_${device.id}_down`
        const prevOfflineKey2 = `status_${device.id}_offline`
        const prevMaintenanceKey = `status_${device.id}_maintenance`
        if (lastAlerts[prevOfflineKey] || lastAlerts[prevOfflineKey2] || lastAlerts[prevMaintenanceKey]) {
          shouldAlert = true
          priority = 'success'
          title = 'Device Back Online'
          message = `Device ${device.name || device.id} is back online`
        }
      }

      if (shouldAlert) {
        addNotification({
          title,
          message,
          priority,
          category: 'deviceOffline',
          deviceId: device.id
        })

        setLastAlerts(prev => ({
          ...prev,
          [alertKey]: currentTime
        }))

        console.log(`Global monitoring: Device status alert triggered for ${device.name || device.id} (${status})`)
      }
    })
  }

  // Initialize monitoring when user claims are available (only once per session)
  useEffect(() => {
    const customer = userClaims?.customer
    if (isMonitoringDisabled || !customer || initializedCustomerRef.current === customer) return

    let isActive = true
    let welcomeTimer

    initializedCustomerRef.current = customer
    console.log(`Global monitoring: Starting for customer ${customer}`)

    // Initial data fetch only
    fetchDevices(customer)
    fetchDeviceStatuses(customer)

    // Request notification permission
    requestPermission().catch((error) => {
      console.warn('Monitoring could not request notification permission:', error.message)
    })
    
    // Send initial system notification (only once per session)
    let hasShownWelcome = true
    try {
      hasShownWelcome = window.sessionStorage.getItem('monitoring_welcome_shown')
    } catch (error) {
      console.warn('Monitoring welcome notice is unavailable:', error.message)
    }

    if (!hasShownWelcome) {
      welcomeTimer = setTimeout(() => {
        if (!isActive) return

        addNotification({
          title: 'Monitoring Active',
          message: 'Device monitoring is ready. Automatic alerts are paused to prevent spam.',
          priority: 'info',
          category: 'system'
        })

        try {
          window.sessionStorage.setItem('monitoring_welcome_shown', 'true')
        } catch (error) {
          console.warn('Could not save monitoring welcome notice:', error.message)
        }
      }, 2000)
    }

    return () => {
      isActive = false
      if (welcomeTimer) clearTimeout(welcomeTimer)
    }
  }, [userClaims?.customer, requestPermission, addNotification])

  // Disabled periodic monitoring to prevent continuous alerts
  // useEffect(() => {
  //   if (!customerName || !isInitialized) return
  //   const monitoringInterval = setInterval(() => {
  //     console.log('Global monitoring: Running periodic checks...')
  //     fetchDevices(customerName)
  //     fetchDeviceStatuses(customerName)
  //   }, 10 * 60 * 1000)
  //   return () => clearInterval(monitoringInterval)
  // }, [customerName, isInitialized])

  // Disabled automatic alert checks to prevent continuous notifications
  // useEffect(() => {
  //   if (devices.length > 0) {
  //     const timer = setTimeout(() => {
  //       console.log(`Global monitoring: Running checks for ${devices.length} devices with ${Object.keys(deviceStatuses).length} status entries`)
  //       checkStockAlerts()
  //       checkDeviceStatusAlerts()
  //     }, 2000)
  //     return () => clearTimeout(timer)
  //   }
  // }, [devices, deviceStatuses])



  // Listen for test notifications
  useEffect(() => {
    const handleTestNotification = (event) => {
      console.log('Global monitoring: Test notification triggered:', event.detail)
      addNotification(event.detail)
    }

    window.addEventListener('testNotification', handleTestNotification)
    return () => window.removeEventListener('testNotification', handleTestNotification)
  }, [addNotification])

  // This component doesn't render anything visible
  return null
}

export default GlobalMonitoring